import { CopyIcon, LinkIcon } from '@/assets/icons'
import Modal from '@components/Modal'
import { copyTextToClipboard } from '@lib/clipboard'
import { tripJoinUrl, tripJoinUrlDisplay } from '@lib/joinLink'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
	CodeLine,
	CodeValue,
	CopiedNote,
	CopyCodeButton,
	CopyLinkButton,
	InviteFootnote,
	LinkIconSlot,
	LinkRow,
	LinkText,
} from './InviteModal.styles'
import { iconSizes } from '@/theme/tokens'

/** How long the "Copied" confirmation stays up before reverting. */
const COPIED_MS = 1500

type CopyTarget = 'link' | 'code'

interface InviteModalProps {
	isOpen: boolean
	onClose: () => void
	/** The trip's 6-letter join code — both the shareable link and the fallback. */
	tripCode: string
}

/**
 * Share-a-trip dialog: link first, join code as the fallback. Opened from the
 * overview hero's invite chip and the members grid's ghost card, which is why it
 * carries no trip state of its own beyond the code.
 */
const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, tripCode }) => {
	const [copied, setCopied] = useState<CopyTarget | null>(null)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const clearTimer = () => {
		if (timerRef.current) clearTimeout(timerRef.current)
		timerRef.current = null
	}

	// The confirmation is transient state, not a fact about the trip — a reopened
	// dialog always starts clean.
	useEffect(() => {
		if (!isOpen) {
			clearTimer()
			setCopied(null)
		}
		return clearTimer
	}, [isOpen])

	const copy = useCallback(async (target: CopyTarget, text: string) => {
		try {
			await copyTextToClipboard(text)
			clearTimer()
			setCopied(target)
			timerRef.current = setTimeout(() => setCopied(null), COPIED_MS)
		} catch {
			toast.error(`Could not copy the ${target}. Copy it manually instead.`)
		}
	}, [])

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Invite members" size="medium">
			<LinkRow>
				<LinkIconSlot>
					<LinkIcon sx={{ fontSize: iconSizes.sm }} />
				</LinkIconSlot>
				<LinkText>{tripJoinUrlDisplay(tripCode)}</LinkText>
				<CopyLinkButton type="button" onClick={() => copy('link', tripJoinUrl(tripCode))}>
					{copied === 'link' ? 'Copied' : 'Copy link'}
				</CopyLinkButton>
			</LinkRow>

			<CodeLine>
				<span>or they can join with the code:</span>
				<CodeValue>{tripCode.toUpperCase()}</CodeValue>
				<CopyCodeButton
					type="button"
					title="Copy code"
					aria-label="Copy code"
					onClick={() => copy('code', tripCode.toUpperCase())}
				>
					{copied === 'code' ? (
						<CopiedNote role="status">Copied</CopiedNote>
					) : (
						<CopyIcon sx={{ fontSize: iconSizes.xs }} />
					)}
				</CopyCodeButton>
			</CodeLine>

			<InviteFootnote>Anyone with the link can join this trip.</InviteFootnote>
		</Modal>
	)
}

export default InviteModal
